import { useCallback, useEffect, useState } from 'react';
import { paymentsAPI } from '../Api/api';

const POLL_INTERVAL_MS = 3000;
const MAX_CHECKS = 20; // about a minute

/**
 * Follows an online order's payment after the customer comes back from the
 * payment page. The provider's webhook (not the return link) marks the order
 * paid, so this keeps checking until the order is paid or closed.
 *
 * phase:
 *   idle      not following (disabled)
 *   checking  waiting for the payment to be confirmed
 *   settled   no longer waiting for payment (paid, or moved on)
 *   awaiting  not paid, and the customer left the payment page — can retry
 *   closed    cancelled without payment (e.g. the checkout expired)
 *   slow      still unconfirmed after about a minute, or the status is unavailable
 */
export function useOnlinePaymentStatus(orderNumber, { enabled, leftPaymentPage }) {
    const [phase, setPhase] = useState('idle');
    const [round, setRound] = useState(0);

    useEffect(() => {
        if (!enabled || !orderNumber) {
            setPhase('idle');
            return undefined;
        }

        let cancelled = false;
        let timer = null;
        let checks = 0;
        setPhase('checking');

        const check = async () => {
            checks += 1;
            try {
                const response = await paymentsAPI.getOrderStatus(orderNumber);
                if (cancelled) return;
                const status = response?.data?.data;
                if (status?.orderStatus === 'cancelled') {
                    setPhase('closed');
                    return;
                }
                if (status?.orderStatus !== 'awaiting-payment') {
                    setPhase('settled');
                    return;
                }
                if (leftPaymentPage) {
                    setPhase('awaiting');
                    return;
                }
            } catch (error) {
                if (cancelled) return;
                // Signed out or not the customer's order: polling won't help.
                const code = error?.response?.status;
                if (code && code < 500) {
                    setPhase('slow');
                    return;
                }
            }

            if (checks >= MAX_CHECKS) {
                setPhase('slow');
                return;
            }
            timer = setTimeout(check, POLL_INTERVAL_MS);
        };

        check();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [enabled, orderNumber, leftPaymentPage, round]);

    const checkAgain = useCallback(() => setRound((value) => value + 1), []);

    return { phase, checkAgain };
}
